import React, { useState, useMemo } from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { TextField, InputAdornment, IconButton } from "@mui/material";
import "./DateTimeRangePicker.css";

const DateTimeRangePicker = ({
  value,
  onChange,
  placeholder = "MM/DD/YYYY hh:mm aa - MM/DD/YYYY hh:mm aa",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempRange, setTempRange] = useState(value || { start: null, end: null });
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectionStep, setSelectionStep] = useState("start");

  const [startTime, setStartTime] = useState({ hour: "12", minute: "00", period: "AM" });
  const [endTime, setEndTime] = useState({ hour: "01", minute: "00", period: "PM" });

  const hours = useMemo(
    () => Array.from({ length: 12 }, (_, i) => (i === 0 ? 12 : i).toString().padStart(2, "0")),
    []
  );
  const minutes = useMemo(
    () => Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, "0")),
    []
  );

  const formatDisplayValue = (range) => {
    if (!range.start && !range.end) return placeholder;

    const formatDateTime = (date) => {
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      const year = date.getFullYear();
      let h = date.getHours();
      const mins = date.getMinutes().toString().padStart(2, "0");
      const ampm = h >= 12 ? "PM" : "AM";
      h = h % 12 || 12;
      return `${month}/${day}/${year} ${h.toString().padStart(2, "0")}:${mins} ${ampm}`;
    };

    if (range.start && range.end) return `${formatDateTime(range.start)} – ${formatDateTime(range.end)}`;
    if (range.start) return `${formatDateTime(range.start)} – MM/DD/YYYY hh:mm aa`;
    return placeholder;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i += 1) days.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) days.push(new Date(year, month, day));
    return days;
  };

  const createDateTime = (date, timeObj) => {
    const hour24 =
      timeObj.period === "AM"
        ? timeObj.hour === "12"
          ? 0
          : Number(timeObj.hour)
        : timeObj.hour === "12"
          ? 12
          : Number(timeObj.hour) + 12;

    const newDate = new Date(date);
    newDate.setHours(hour24, Number(timeObj.minute), 0, 0);
    return newDate;
  };

  const timeDisabled =
    (selectionStep === "start" && !tempRange.start) ||
    (selectionStep === "end" && !tempRange.end);

  const handleDateSelect = (date) => {
    if (selectionStep === "start") {
      const newDateTime = createDateTime(date, startTime);
      setTempRange({ start: newDateTime, end: null });
      return;
    }
    const newEndDateTime = createDateTime(date, endTime);
    if (tempRange.start && newEndDateTime < tempRange.start) {
      setTempRange({ start: newEndDateTime, end: tempRange.start });
      setEndTime(startTime);
      setStartTime(endTime);
    } else {
      setTempRange((prev) => ({ ...prev, end: newEndDateTime }));
    }
  };

  const handleTimeChange = (type, val) => {
    if (timeDisabled) return;

    if (selectionStep === "start") {
      const next = { ...startTime, [type]: val };
      setStartTime(next);
      if (tempRange.start) {
        const newDateTime = createDateTime(tempRange.start, next);
        setTempRange((prev) => ({ ...prev, start: newDateTime }));
      }
      return;
    }

    const next = { ...endTime, [type]: val };
    setEndTime(next);
    if (tempRange.end) {
      const newDateTime = createDateTime(tempRange.end, next);
      setTempRange((prev) => ({ ...prev, end: newDateTime }));
    }
  };

  const handlePrimary = () => {
    if (tempRange.start && !tempRange.end) {
      setSelectionStep("end");
      return;
    }
    if (tempRange.start && tempRange.end) {
      onChange?.(tempRange);
      setIsOpen(false);
      setSelectionStep("start");
    }
  };

  const handleCancel = () => {
    setTempRange(value || { start: null, end: null });
    setSelectionStep("start");
    setIsOpen(false);
  };

  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(direction === "prev" ? prev.getMonth() - 1 : prev.getMonth() + 1);
      return newMonth;
    });
  };

  const days = getDaysInMonth(currentMonth);
  const monthYear = currentMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const currentTime = selectionStep === "start" ? startTime : endTime;

  const isSameDay = (d1, d2) => d1?.toDateString() === d2?.toDateString();

  return (
    <div className="datetime-range-picker">
      <TextField
        label="Date"
        variant="outlined"
        fullWidth
        value={formatDisplayValue(value || { start: null, end: null })}
        onClick={() => {
          const next = !isOpen;
          setIsOpen(next);
          if (next) setSelectionStep("start");
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => {
                  const next = !isOpen;
                  setIsOpen(next);
                  if (next) setSelectionStep("start");
                }}
              >
                <CalendarMonthIcon />
              </IconButton>
            </InputAdornment>
          ),
          readOnly: true,
        }}
      />

      {isOpen && (
        <div className="picker-popup">
          <div className="picker-content picker-content--calendar-left">
            {/* CALENDAR (LEFT) */}
            <div className="calendar-section">
              <div className="month-nav">
                <button onClick={() => navigateMonth("prev")} className="nav-btn">‹</button>
                <h3 className="month-title">{monthYear}</h3>
                <button onClick={() => navigateMonth("next")} className="nav-btn">›</button>
              </div>

              <div className="calendar-grid">
                <div className="weekdays">
                  {["S", "M", "T", "W", "T", "F", "S"].map((d) => (
                    <div key={d} className="weekday">{d}</div>
                  ))}
                </div>

                <div className="days-grid">
                  {days.map((day, index) => (
                    <div key={index} className="day-cell">
                      {day && (
                        <button
                          onClick={() => handleDateSelect(day)}
                          className={`day-btn ${isSameDay(day, tempRange.start) ? "selected-start" : ""
                            } ${isSameDay(day, tempRange.end) ? "selected-end" : ""}`}
                        >
                          {day.getDate()}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* TIME (RIGHT) */}
            <div className={`time-section ${timeDisabled ? "disabled" : ""}`}>
              <div className="time-header">
                <p className="time-label">
                  {selectionStep === "start" ? "Set Start Time" : "Set End Time"}
                </p>
                {tempRange.start && selectionStep === "end" && (
                  <p className="start-time-display">
                    Start: {formatDisplayValue({ start: tempRange.start, end: null }).split(" – ")[0]}
                  </p>
                )}
              </div>

              <div className="time-columns">
                <div className="time-column">
                  <div className="time-scroll">
                    {hours.map((hour) => (
                      <button
                        key={hour}
                        onClick={() => handleTimeChange("hour", hour)}
                        className={`time-btn ${currentTime.hour === hour ? "selected" : ""}`}
                        disabled={timeDisabled}
                        aria-disabled={timeDisabled}
                      >
                        {hour}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="time-column">
                  <div className="time-scroll">
                    {minutes.map((minute) => (
                      <button
                        key={minute}
                        onClick={() => handleTimeChange("minute", minute)}
                        className={`time-btn ${currentTime.minute === minute ? "selected" : ""}`}
                        disabled={timeDisabled}
                        aria-disabled={timeDisabled}
                      >
                        {minute}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="time-column">
                  <div className="period-buttons">
                    <button
                      onClick={() => handleTimeChange("period", "AM")}
                      className={`period-btn ${currentTime.period === "AM" ? "selected" : ""}`}
                      disabled={timeDisabled}
                      aria-disabled={timeDisabled}
                    >
                      AM
                    </button>
                    <button
                      onClick={() => handleTimeChange("period", "PM")}
                      className={`period-btn ${currentTime.period === "PM" ? "selected" : ""}`}
                      disabled={timeDisabled}
                      aria-disabled={timeDisabled}
                    >
                      PM
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="action-buttons">
            <div style={{ marginRight: "auto" }}>
              <button
                onClick={() => {
                  const todayMidnight = new Date();
                  todayMidnight.setHours(0, 0, 0, 0);
                  const tomorrowMidnight = new Date(todayMidnight);
                  tomorrowMidnight.setDate(todayMidnight.getDate() + 1);

                  setTempRange({ start: todayMidnight, end: tomorrowMidnight });
                  setStartTime({ hour: "12", minute: "00", period: "AM" });
                  setEndTime({ hour: "12", minute: "00", period: "AM" });
                  setSelectionStep("start");
                  setCurrentMonth(new Date());
                  onChange?.({ start: todayMidnight, end: tomorrowMidnight });
                }}
                className="cancel-btn"
              >
                RESET
              </button>
            </div>

            <button onClick={handleCancel} className="cancel-btn">CANCEL</button>
            <button
              onClick={handlePrimary}
              disabled={!tempRange.start}
              className="apply-btn"
            >
              {tempRange.start && !tempRange.end ? "SELECT END DATE" :
                tempRange.start && tempRange.end ? "APPLY" : "NEXT"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateTimeRangePicker;
